/** 경쟁사 탭 — an app's competitors (S3, `/apps/:id/competitors`).
 *
 *  What the old tab did, in the same order: find candidates from the tracked
 *  keywords, add one by iTunes track id, the table of linked competitors, and
 *  below it the candidates (sorted by review mass, add one or all). Removing a
 *  competitor now asks first.
 *
 *  Three things go out to iTunes and are never pressed in a dev server: 자동
 *  찾기, 추가 by track id, and adding a candidate (the server looks the app up
 *  by its track id).
 *
 *  추정 월 수익 replaced the Sensor Tower 월 매출 column (empty for almost
 *  every app): a range from US rating growth, with the US top-grossing chart
 *  next to it. A row opens the drawer with the working and the tier pick;
 *  `?rev=<app id>` keeps it open across a reload. */

import { useState } from 'react'
import { Plus, Search, Trash2, Users } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type CompetitorRow, type ITunesResult } from '@/api/client'
import { qk, useCompetitors } from '@/lib/queries'
import { EstimateCell, estimateShort, GrossingCell, RevenueDrawer, SelfLine } from './RevenueEstimate'
import {
  AppIcon,
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  errorText,
  IconButton,
  Input,
  useQueryParam,
  useToast,
  type Column,
} from '@/ui'

export function CompetitorsTab({ appId }: { appId: number }) {
  const qc = useQueryClient()
  const toast = useToast()
  const list = useCompetitors(appId)
  const refresh = () => qc.invalidateQueries({ queryKey: qk.competitors(appId) })

  const [trackIdInput, setTrackIdInput] = useState('')
  const addByTrackId = useMutation({
    mutationFn: (trackId: number) => api.addCompetitor(appId, { track_id: trackId, country: 'us' }),
    onSuccess: (r) => {
      setTrackIdInput('')
      toast.success(`경쟁사 “${r.app.name}”을 추가했다`)
      refresh()
    },
    onError: (e) => toast.error(errorText(e)),
  })

  const find = useMutation({
    mutationFn: () => api.findCompetitors(appId),
    onError: (e) => toast.error(errorText(e)),
  })
  const addFromCandidate = useMutation({
    mutationFn: (m: ITunesResult) => api.addCompetitor(appId, { track_id: m.trackId, country: 'us' }),
    onSuccess: (r) => {
      toast.success(`경쟁사 “${r.app.name}”을 추가했다`)
      refresh()
    },
    onError: (e) => toast.error(errorText(e)),
  })
  const bulkAdd = useMutation({
    mutationFn: (trackIds: number[]) => api.bulkAddCompetitors(appId, trackIds, 'us'),
    onSuccess: (r) => {
      toast.success(`${r.added.length}개를 추가했다`)
      refresh()
    },
    onError: (e) => toast.error(errorText(e)),
  })

  const [toRemove, setToRemove] = useState<CompetitorRow | null>(null)
  const remove = useMutation({
    mutationFn: (linkId: number) => api.deleteCompetitor(linkId),
    onSuccess: () => {
      toast.success('경쟁사를 제거했다')
      setToRemove(null)
      refresh()
    },
    onError: (e) => toast.error(errorText(e)),
  })

  const [revParam, setRevParam] = useQueryParam('rev')

  const columns: Column<CompetitorRow>[] = [
    {
      key: 'app',
      header: '앱',
      card: 'title',
      className: 'max-w-[220px]',
      cell: (c) => (
        <span className="flex min-w-0 items-center gap-2.5">
          <AppIcon src={c.app.icon_url} name={c.app.name} size={36} />
          <span className="truncate font-medium">{c.app.name}</span>
        </span>
      ),
    },
    {
      key: 'developer',
      header: '개발사',
      card: 'sub',
      wideOnly: true,
      className: 'max-w-[200px] truncate text-muted-foreground',
      cell: (c) => c.app.developer || '—',
    },
    {
      key: 'rating',
      header: '평점',
      align: 'right',
      card: 'hidden',
      className: 'whitespace-nowrap tabular-nums',
      cell: (c) => (c.app.rating ? `★ ${c.app.rating.toFixed(2)}` : <span className="text-muted-foreground">—</span>),
    },
    {
      key: 'reviews',
      header: '리뷰',
      align: 'right',
      card: 'hidden',
      className: 'tabular-nums',
      cell: (c) => (c.app.rating_count ? c.app.rating_count.toLocaleString('ko-KR') : <span className="text-muted-foreground">—</span>),
    },
    {
      key: 'price',
      header: '가격',
      align: 'right',
      card: 'hidden',
      className: 'whitespace-nowrap tabular-nums',
      cell: (c) => (c.app.price ? `${c.app.currency} ${c.app.price}` : <span className="text-muted-foreground">무료</span>),
    },
    {
      key: 'estimate',
      header: '추정 월 수익',
      align: 'right',
      card: 'hidden',
      className: 'whitespace-nowrap tabular-nums',
      cell: (c) => <EstimateCell e={c.estimate} />,
    },
    {
      key: 'grossing',
      header: '매출 순위',
      align: 'right',
      card: 'hidden',
      className: 'whitespace-nowrap tabular-nums',
      cell: (c) => <GrossingCell e={c.estimate} />,
    },
    // the phone's two lines for the four numbers
    {
      key: 'ratingLine',
      header: '평점 · 리뷰',
      cardOnly: true,
      className: 'tabular-nums',
      cell: (c) =>
        c.app.rating ? (
          <span className="tabular-nums">
            ★ {c.app.rating.toFixed(2)}
            {c.app.rating_count > 0 && <span className="text-muted-foreground"> · {c.app.rating_count.toLocaleString('ko-KR')}</span>}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'moneyLine',
      header: '가격 · 추정 월 수익',
      cardOnly: true,
      cell: (c) => (
        <span className="tabular-nums">
          {c.app.price ? `${c.app.currency} ${c.app.price}` : '무료'}
          {c.estimate ? <span className="text-muted-foreground"> · {estimateShort(c.estimate)}</span> : null}
        </span>
      ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">처리</span>,
      width: '56px',
      align: 'right',
      card: 'actions',
      cell: (c) => (
        <IconButton
          icon={Trash2}
          size="sm"
          variant="quietDanger"
          label={`${c.app.name} 제거`}
          onClick={(e) => {
            e.stopPropagation()
            setToRemove(c)
          }}
        />
      ),
    },
  ]

  const candidates = find.data?.candidates ?? []
  const items = list.data?.items
  const self = list.data?.self
  const revId = Number(revParam)
  const revRow = items?.find((c) => c.app.id === revId)
  const revApp = revRow?.app ?? (self && self.app.id === revId ? self.app : undefined)
  const revEstimate = revRow?.estimate ?? (self && self.app.id === revId ? self.estimate : undefined)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <p className="max-w-2xl text-table text-muted-foreground">
          같은 시장에서 겨루는 앱. 이 앱들의 지표가 경쟁 점수와 진입 위험 점수에 들어간다.
        </p>
        <Button
          variant="primary"
          icon={Search}
          loading={find.isPending}
          onClick={() => find.mutate()}
          title="추적 중인 키워드의 상위 앱에서 후보를 찾는다 (iTunes 조회)"
        >
          {find.isPending ? '찾는 중' : '자동 찾기'}
        </Button>
      </div>

      <form
        className="flex flex-wrap gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          const n = Number(trackIdInput.trim())
          if (Number.isInteger(n) && n > 0) addByTrackId.mutate(n)
        }}
      >
        <Input
          aria-label="트랙 ID"
          inputMode="numeric"
          className="min-w-[220px] flex-1"
          placeholder="iTunes 트랙 ID를 붙여 넣는다 (예: 553834731)"
          value={trackIdInput}
          onChange={(e) => setTrackIdInput(e.target.value)}
        />
        <Button type="submit" icon={Plus} loading={addByTrackId.isPending} disabled={!trackIdInput.trim()}>
          추가
        </Button>
      </form>

      {list.data?.model && (items?.length ?? 0) > 0 && (
        <div className="space-y-1 rounded-lg border border-border bg-card px-4 py-3">
          <p className="text-table text-muted-foreground">
            추정 월 수익 = 미국 평점 증가(최근 90일, 월 환산) × 평점 1개당 다운로드 100 × 다운로드당 매출 × 실수령 75%. 굵은 숫자가
            가운데 값, 아래가 다운로드 50–200 범위다. 앱 하나씩은 몇 배씩 틀릴 수 있어 순서와 규모를 보는 데 쓴다. 행을 누르면 근거를
            보고 매출 등급을 고른다.
          </p>
          {self && <SelfLine app={self.app} e={self.estimate} onOpen={() => setRevParam(String(self.app.id))} />}
        </div>
      )}

      <DataTable
        label="경쟁사"
        columns={columns}
        rows={items}
        rowKey={(c) => c.link.id}
        onRowClick={list.data?.model ? (c) => setRevParam(String(c.app.id)) : undefined}
        loading={list.isLoading}
        error={list.error}
        onRetry={() => list.refetch()}
        empty={
          <EmptyState
            icon={Users}
            title="연결된 경쟁사가 없다"
            description="자동 찾기로 추적 키워드의 상위 앱에서 후보를 찾거나, 위에 iTunes 트랙 ID를 붙여 넣는다."
          />
        }
      />

      {find.data && candidates.length === 0 && <p className="text-table text-muted-foreground">찾은 후보가 없다.</p>}
      {candidates.length > 0 && (
        <section className="overflow-hidden rounded-lg border border-border bg-card" aria-label="후보">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
            <div>
              <h2 className="text-section font-semibold">후보</h2>
              <p className="mt-0.5 text-meta text-muted-foreground">
                {candidates.length}개 · 리뷰 많은 순
                {bulkAdd.data && ` · 마지막 일괄 추가: ${bulkAdd.data.added.length}개 추가, ${bulkAdd.data.skipped.length}개 건너뜀`}
              </p>
            </div>
            <Button
              icon={Plus}
              loading={bulkAdd.isPending}
              onClick={() => bulkAdd.mutate(candidates.map((m) => m.trackId))}
            >
              {`전부 추가 (${candidates.length})`}
            </Button>
          </div>
          <ul className="divide-y divide-border">
            {candidates.map((m) => (
              <li key={m.trackId} className="flex items-center gap-3 px-4 py-2.5">
                <AppIcon src={m.artworkUrl100} name={m.trackName} size={40} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-body font-medium">{m.trackName}</div>
                  <div className="truncate text-meta text-muted-foreground">
                    {m.sellerName}
                    {typeof m.averageUserRating === 'number' &&
                      ` · ★${m.averageUserRating.toFixed(2)} (${(m.userRatingCount ?? 0).toLocaleString('ko-KR')})`}
                  </div>
                </div>
                <Button
                  size="sm"
                  aria-label={`${m.trackName} 추가`}
                  disabled={addFromCandidate.isPending}
                  onClick={() => addFromCandidate.mutate(m)}
                >
                  추가
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <RevenueDrawer
        open={!!revApp}
        onClose={() => setRevParam('')}
        app={revApp}
        e={revEstimate}
        tiers={list.data?.model?.tiers ?? {}}
        ownerAppId={appId}
      />

      <ConfirmDialog
        open={toRemove !== null}
        onOpenChange={(o) => !o && setToRemove(null)}
        title={`“${toRemove?.app.name ?? ''}”을 경쟁사에서 제거할까`}
        description="이 앱과의 연결만 끊는다. 점수는 다음에 다시 계산할 때 바뀐다."
        confirmLabel="제거"
        confirmVariant="destructive"
        loading={remove.isPending}
        onConfirm={async () => {
          if (!toRemove) return
          try {
            await remove.mutateAsync(toRemove.link.id)
          } catch {
            /* the toast already says why */
          }
        }}
      />
    </div>
  )
}
